import React, { useState } from 'react';
import styled from 'styled-components';
import { Menu, X, Zap } from 'lucide-react';

const HeaderContainer = styled.header`
  background-color: #ffffff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  width: 100vw;
  max-width: 100%;
  overflow-x: hidden;
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem 1rem;
    min-height: 70px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    gap: 0.3rem;
  }
`;

const LogoIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #ff6900 0%, #ff8533 100%);
  border-radius: 8px;
  position: relative;
  box-shadow: 0 2px 8px rgba(255, 105, 0, 0.3);

  svg {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const LogoText = styled.div`
  font-size: 1.8rem;
  font-weight: bold;
  color: #333;
  letter-spacing: -0.5px;

  .rj {
    color: #ff6900;
  }
  
  .spark {
    color: #333;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const LogoSubtext = styled.div`
  font-size: 0.7rem;
  color: #666;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: -2px;

  @media (max-width: 768px) {
    font-size: 0.6rem;
  }
`;

const NavLinks = styled.ul<{ $isOpen: boolean }>`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 2rem;

  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'flex' : 'none'} !important;
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    width: 100vw;
    max-width: 100%;
    background-color: #ffffff;
    flex-direction: column;
    padding: 2rem 1rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    z-index: 999;
    gap: 1.5rem;
    box-sizing: border-box;
    overflow-x: hidden;
  }
`;

const NavLink = styled.a`
  text-decoration: none;
  color: #333;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: #ff6900;
  }

  @media (max-width: 768px) {
    padding: 0.75rem 0;
    font-size: 1.1rem;
    border-bottom: 1px solid #eee;
    
    &:last-child {
      border-bottom: none;
    }
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #333;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  flex-shrink: 0;
  min-width: 44px;
  min-height: 44px;
  z-index: 1001;
  position: relative;

  &:hover {
    background-color: #f5f5f5;
  }

  &:focus {
    outline: 2px solid #ff6900;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    display: flex !important;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 480px) {
    min-width: 40px;
    min-height: 40px;
    font-size: 1.3rem;
  }
`;

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  return (
    <HeaderContainer>
      <Nav>
        <LogoContainer onClick={scrollToTop}>
          <LogoIcon>
            <Zap color="white" />
          </LogoIcon>
          <div>
            <LogoText>
              <span className="rj">RJ</span><span className="spark">Spark</span>
            </LogoText>
            <LogoSubtext>Data Engineering</LogoSubtext>
          </div>
        </LogoContainer>
        <NavLinks $isOpen={isMenuOpen}>
          <li><NavLink onClick={() => scrollToSection('home')}>Home</NavLink></li>
          <li><NavLink onClick={() => scrollToSection('services')}>Services</NavLink></li>
          <li><NavLink onClick={() => scrollToSection('about')}>About</NavLink></li>
          <li><NavLink onClick={() => scrollToSection('contact')}>Contact</NavLink></li>
        </NavLinks>
        <MenuButton onClick={toggleMenu}>
          {isMenuOpen ? <X /> : <Menu />}
        </MenuButton>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;